// Estado de autenticación de la web de oficina. Al cargar consulta
// /api/auth/me para saber si ya hay sesión; expone login/logout.
import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { api, type Usuario } from './api.js';

interface AuthState {
  user: Usuario | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthCtx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<Usuario | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .me()
      .then((r) => setUser(r.user))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  const login = async (email: string, password: string) => {
    const r = await api.login(email, password);
    setUser(r.user);
  };

  const logout = async () => {
    await api.logout().catch(() => undefined);
    setUser(null);
  };

  const refresh = async () => {
    const r = await api.me().catch(() => null);
    setUser(r?.user ?? null);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, logout, refresh }}>{children}</AuthCtx.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth debe usarse dentro de <AuthProvider>');
  return ctx;
}
